<script setup lang="ts">
/**
 * PMS shell — composes PmsHeader + PmsSideNav with a slot for the
 * route-level page content. Layout-only (the `.fz-shell` grid in
 * `design-system/shell.css`).
 *
 * Slot-driven. Phase-2 view code passes the nav model and binds the
 * active id to the current route. The shell itself does NOT touch the
 * router, the auth store, or `usePermissions`.
 */
import { ref, computed } from 'vue'
import PmsHeader, { type PmsHeaderNavItem } from './PmsHeader.vue'
import PmsSideNav, { type PmsSideNavSection } from './PmsSideNav.vue'
import NotificationContainer from './NotificationContainer.vue'

const props = withDefaults(
  defineProps<{
    headerNavItems: PmsHeaderNavItem[]
    activeHeaderId?: string
    sideNavSections: PmsSideNavSection[]
    activeSideNavId?: string
    avatarInitials?: string
    avatarTitle?: string
    /** Initial state of the side-nav. Subsequent toggles are component-local. */
    defaultNavOpen?: boolean
  }>(),
  {
    defaultNavOpen: true,
    avatarInitials: '',
    avatarTitle: '',
    activeHeaderId: undefined,
    activeSideNavId: undefined,
  },
)

const emit = defineEmits<{
  selectHeader: [id: string]
  selectSideNav: [id: string]
  notificationsClick: []
  helpClick: []
  avatarClick: []
}>()

const navOpen = ref(props.defaultNavOpen)

const shellClass = computed(() => ['fz-shell', { 'fz-shell--collapsed': !navOpen.value }])

function toggleNav() {
  navOpen.value = !navOpen.value
}
</script>

<template>
  <div :class="shellClass">
    <div class="fz-shell__header">
      <PmsHeader
        :nav-items="headerNavItems"
        :active-id="activeHeaderId"
        :avatar-initials="avatarInitials"
        :avatar-title="avatarTitle"
        :nav-open="navOpen"
        @toggle-nav="toggleNav"
        @select-nav="(id) => emit('selectHeader', id)"
        @notifications-click="emit('notificationsClick')"
        @help-click="emit('helpClick')"
        @avatar-click="emit('avatarClick')"
      />
    </div>
    <div class="fz-shell__nav">
      <PmsSideNav
        :sections="sideNavSections"
        :active-id="activeSideNavId"
        :collapsed="!navOpen"
        @select="(id) => emit('selectSideNav', id)"
      />
    </div>
    <main class="fz-shell__main">
      <slot />
    </main>
    <NotificationContainer />
  </div>
</template>
